/**
 * The idle game loop. Advances the simulation on a fixed tick, independent of
 * frame rate. The renderer reads gameState each frame and animates toward it —
 * it never ticks. From Phase 2 on, each tick resolves a combat round.
 */

import { bus } from './events.js';
import { resolveRound, handleRevives, heroDerivedStats } from './combat.js';
import { partyPlayable } from './inventory.js';
import { Rng } from './rng.js';

export const TICK_MS = 1800; // one combat round every 1.8s — slower, readable battles (tunable)

export class GameLoop {
  /**
   * @param {object} state the canonical gameState
   * @param {{classes:object, maps:object, items?:object, rarity?:object}} data balance tables
   */
  constructor(state, data) {
    this.state = state;
    this.data = data;
    this.rng = new Rng(state.rngSeed);
    this._timer = null;
    this._running = false;
    this._syncHeroPools();
  }

  /** Ensure each hero's HP pool matches its derived max (after load/level). */
  _syncHeroPools() {
    for (const hero of this.state.party) {
      const hs = heroDerivedStats(hero, this.data.classes, hero.equipped);
      hero.maxHp = hs.maxHp;
      if (hero.hp == null || hero.hp > hs.maxHp) hero.hp = hs.maxHp;
      if (hero.alive == null) hero.alive = true;
    }
  }

  /** Start ticking on the fixed interval. */
  start() {
    if (this._running) return;
    this._running = true;
    this._timer = setInterval(() => this.tick(), TICK_MS);
    bus.emit('loop:start');
  }

  /** Stop ticking (e.g. for offline/pause). */
  stop() {
    if (!this._running) return;
    this._running = false;
    clearInterval(this._timer);
    this._timer = null;
    bus.emit('loop:stop');
  }

  /**
   * Advance one round of simulation: revives, then a combat round.
   * Each resulting event is re-emitted on the shared bus for the renderer.
   */
  tick() {
    const s = this.state;
    s.stats.ticks += 1;

    // Combat runs while at least one hero is playable; downed/locked heroes sit
    // out until their individual lockout elapses (handled by handleRevives).
    const events = handleRevives(s, this.data.classes);
    if (partyPlayable(s)) {
      events.push(...resolveRound(s, this.data, this.rng));
    }
    for (const ev of events) bus.emit(`combat:${ev.type}`, ev);

    bus.emit('tick', { tick: s.stats.ticks, events, locked: !partyPlayable(s) });
    bus.emit('state:dirty');
  }

  // NOTE: there is intentionally no offline simulation. The dungeon only
  // fights while the page (or its PiP window) is open — keeping it open IS
  // the game. Closing the tab pauses the world where it stands.
}
